import { createCommand, type NodeKey, type TextFormatType } from "lexical";
import type { Component } from "vue";

import {
  RecommendActionItemsIcon,
  RecommendBrainstormIcon,
  RecommendConvertToTasksIcon,
  RecommendImproveIcon,
  RecommendPropertiesIcon,
  RecommendSimplifyIcon,
  RecommendSplitIcon,
  RecommendSubtasksIcon,
  RecommendTranslateIcon,
  RecommendWriteIcon,
} from "~/icons";
import { Language, RecommendationAction, RecommendationKind, TypeaheadSpecialEntity } from "~/shared/enums";
import type { User } from "~/shared/types";
import { CONTAINS_EMAIL_REGEX, CONTAINS_URL_INCLUDING_LOCAL_REGEX } from "~/utils/validation";

export const TEXT_FORMATS: TextFormatType[] = ["bold", "italic", "underline", "strikethrough", "highlight", "code"];

const URL_SCHEMA_START_REGEX = /^https?:\/\//;

export const URL_MATCHERS = [
  (text: string) => {
    const match = CONTAINS_EMAIL_REGEX.exec(text);
    return (
      match && {
        index: match.index,
        length: match[0].length,
        text: match[0],
        url: `mailto:${match[0]}`,
      }
    );
  },
  (text: string) => {
    const match = CONTAINS_URL_INCLUDING_LOCAL_REGEX.exec(text);
    if (!match) {
      return null;
    }

    const content = match[0];
    return {
      index: match.index,
      length: content.length,
      text: content,
      url: URL_SCHEMA_START_REGEX.test(content) ? content : `https://${content}`,
    };
  },
];

const escapeRegexLiteral = (value: string): string => value.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");

const createSuffixRegex = (value: string, ignoreCase = false): RegExp =>
  new RegExp(`${escapeRegexLiteral(value)}$`, ignoreCase ? "i" : undefined);

export const TEXT_REPLACEMENT_RULES: Array<[RegExp, string, boolean?]> = [
  [createSuffixRegex("..."), "…"],
  [createSuffixRegex("-->"), "→"],
  [createSuffixRegex("<--"), "←"],
  [/(?:<--|←)>$/, "↔"],
  [createSuffixRegex("==>"), "⇒"],
  [/(?:≤|<=)=$/, "⇐"],
  [/(?:<==|⇐)>$/, "⇔"],
  [createSuffixRegex("--"), "—"],
  [createSuffixRegex("<="), "≤"],
  [createSuffixRegex(">="), "≥"],
  [createSuffixRegex("+/-"), "±"],
  [createSuffixRegex("!="), "≠"],
  [createSuffixRegex("~="), "≈"],
  [createSuffixRegex("(p)", true), "§"],
  [createSuffixRegex("(c)", true), "©"],
  [createSuffixRegex("(r)", true), "®"],
  [createSuffixRegex("(sm)", true), "℠"],
  [createSuffixRegex("(tm)", true), "™"],
  [createSuffixRegex("c/o", true), "℅", true],
  [/:-?\)$/, "🙂", true],
  [/;-?\)$/, "😉", true],
  [/:-?\($/, "🙁", true],
  [/:-?d$/i, "😃", true],
  [/:p$/i, "😛", true],
  [/:-p$/i, "😜", true],
  [/;-?p$/i, "😜", true],
  [/:-?o$/i, "😮", true],
  [createSuffixRegex("<3"), "❤️", true],
  [createSuffixRegex("</3"), "💔", true],
  [createSuffixRegex("1/2"), "½", true],
  [createSuffixRegex("1/3"), "⅓", true],
  [createSuffixRegex("1/4"), "¼", true],
  [createSuffixRegex("1/5"), "⅕", true],
  [createSuffixRegex("1/6"), "⅙", true],
  [createSuffixRegex("1/8"), "⅛", true],
  [createSuffixRegex("2/3"), "⅔", true],
  [createSuffixRegex("2/5"), "⅖", true],
  [createSuffixRegex("3/4"), "¾", true],
  [createSuffixRegex("3/5"), "⅗", true],
  [createSuffixRegex("3/8"), "⅜", true],
  [createSuffixRegex("4/5"), "⅘", true],
  [createSuffixRegex("5/6"), "⅚", true],
  [createSuffixRegex("5/8"), "⅝", true],
  [createSuffixRegex("7/8"), "⅞", true],
];

export const RECOMMENDATION_KIND_TO_ICON_MAP = new Map<RecommendationKind, Component>([
  [RecommendationKind.ACTION_ITEMS, RecommendActionItemsIcon],
  [RecommendationKind.CONVERT_TO_TASKS, RecommendConvertToTasksIcon],
  [RecommendationKind.BRAINSTORM, RecommendBrainstormIcon],
  [RecommendationKind.PROPERTIES, RecommendPropertiesIcon],
  [RecommendationKind.IMPROVE, RecommendImproveIcon],
  [RecommendationKind.SIMPLIFY, RecommendSimplifyIcon],
  [RecommendationKind.SPLIT, RecommendSplitIcon],
  [RecommendationKind.SUBTASKS, RecommendSubtasksIcon],
  [RecommendationKind.TRANSLATE, RecommendTranslateIcon],
  [RecommendationKind.WRITE, RecommendWriteIcon],
]);

export const RECOMMENDATION_KINDS_THAT_REPLACE = new Set([
  RecommendationKind.IMPROVE,
  RecommendationKind.SIMPLIFY,
  RecommendationKind.TRANSLATE,
]);

export const RECOMMENDATION_KINDS_OVERLAY = new Set<RecommendationKind>([
  RecommendationKind.TRANSLATE,
  RecommendationKind.IMPROVE,
  RecommendationKind.SIMPLIFY,
  RecommendationKind.SPLIT,
  RecommendationKind.CONVERT_TO_TASKS,
  RecommendationKind.PROPERTIES,
  RecommendationKind.SUBTASKS,
  RecommendationKind.CUSTOM,
]);

export const RECOMMENDATION_KINDS_ALLOWED_ON_EMPTY_EDITOR = new Set<RecommendationKind>([
  RecommendationKind.BRAINSTORM,
  RecommendationKind.CUSTOM,
  RecommendationKind.WRITE,
]);

export const EVENT_BLUR_OVERALL = createCommand("EVENT_BLUR_OVERALL");
export const EVENT_SUBSCRIBE_USER = createCommand<User>("EVENT_SUBSCRIBE_USER");
export const EVENT_SPLIT_TASK = createCommand("EVENT_SPLIT_TASK");
export const EVENT_CONVERT_TO_TASKS = createCommand("EVENT_CONVERT_TO_TASKS");
export const EVENT_GENERATE_RECOMMENDATION = createCommand<{
  kind: RecommendationKind | Language;
  parentKey: string | null;
}>("EVENT_GENERATE_RECOMMENDATION");
export const EVENT_CLEANUP_RECOMMENDATION_NODE = createCommand<{
  key: string;
  action: RecommendationAction;
  recommendationDuid?: string;
}>("EVENT_CLEANUP_RECOMMENDATION_NODE");
export const EVENT_OPEN_RECOMMENDATIONS = createCommand<void>("EVENT_OPEN_RECOMMENDATIONS");
export const EVENT_OPEN_RECOMMENDATION_OVERLAY = createCommand<{
  markId: string | null;
  kind: RecommendationKind;
  language?: Language;
  selectedMarkdown: string | null;
}>("EVENT_OPEN_RECOMMENDATION_OVERLAY");
export const EVENT_CREATE_TASK_TASK_RELATES_TO_RELATIONSHIP = createCommand<string>(
  "EVENT_CREATE_TASK_TASK_RELATES_TO_RELATIONSHIP"
);
export const EVENT_CREATE_TASK_DOC_RELATIONSHIP = createCommand<{
  specialEntity: TypeaheadSpecialEntity;
  entityDuid: string;
}>("EVENT_CREATE_TASK_DOC_RELATIONSHIP");
export const EVENT_CREATE_DOC_DOC_RELATIONSHIP = createCommand<string>("EVENT_CREATE_DOC_DOC_RELATIONSHIP");

export const EVENT_INSERT_TOGGLE_COMMAND = createCommand("EVENT_INSERT_TOGGLE_COMMAND");
export const EVENT_TOGGLE_TOGGLE_COMMAND = createCommand<NodeKey>("EVENT_TOGGLE_TOGGLE_COMMAND");
export const EVENT_CHANGE_TOGGLE_EMPTY = createCommand("EVENT_CHANGE_TOGGLE_EMPTY");

export const EVENT_INSERT_HORIZONTAL_RULE = createCommand("EVENT_INSERT_HORIZONTAL_RULE");

export const EVENT_FORMAT_TEXT_COLOR = createCommand<string>("EVENT_FORMAT_TEXT_COLOR");
export const EVENT_FORMAT_BACKGROUND_COLOR = createCommand<string>("EVENT_FORMAT_BACKGROUND_COLOR");

export const LEXICAL_OUTSIDE_CLASS = "dart-lexical-outside";

export const RECOMMENDATION_SECTION_KEY_AI = "ai";

export const TASK_DETAIL_TELEPORT_KEY = "rightbar-toolbar";
export const TCM_TELEPORT_KEY = "tcm-toolbar";
export const DOC_TELEPORT_KEY = "doc-toolbar";
export const FORM_TELEPORT_KEY = "form-toolbar";
export const DEFAULT_EDITOR_TELEPORT_KEY = "default-editor-toolbar";
export const AI_CHAT_TELEPORT_KEY = "ai-chat-toolbar";
export const AGENT_EDITOR_TELEPORT_KEY = "agent-editor-toolbar";
export const AGENT_SCHEDULE_EDITOR_TELEPORT_KEY = "agent-schedule-editor-toolbar";
export const SKILL_EDITOR_TELEPORT_KEY = "skill-editor-toolbar";
export const TASK_DETAIL_TITLE_WRAPPER_ID = "task-detail-title-wrapper";
export const EDITOR_MIN_HEIGHT_PX = 56;

export const RECOMMENDATION_BOX_CLASSES =
  "-mt-px -mb-1 rounded-lg border border-md hover:border-hvy focus-within:border-hvy bg-lt shadow-recommendation-box pt-2";
